(function() {
  'use strict';

  // Configuration
  const API_BASE = 'https://app.bepivotal.co.uk/api/v1';
  const STOREFRONT_KEY = '7a2918b913ae5bf9e75103ea994aba1df813abd1b185354f3b69304acfd4a17d';
  const SESSION_TOKEN_KEY = 'wishlist_session_token';

  // State
  let state = {
    isLoggedIn: false,
    customerId: null,
    sessionToken: null,
    rooms: [],
    currentRoomId: null,
    products: []
  };

  // DOM Elements
  const elements = {
    guestPrompt: document.querySelector('.guest-login-prompt'),
    roomsTabs: document.getElementById('roomsTabs'),
    addRoomBtn: document.getElementById('addRoomBtn'),
    currentRoomName: document.getElementById('currentRoomName'),
    roomActions: document.getElementById('roomActions'),
    roomActionsCustom: document.getElementById('roomActionsCustom'),
    clearAllBtn: document.getElementById('clearAllBtn'),
    productsGrid: document.getElementById('productsGrid'),
    emptyState: document.getElementById('emptyState'),
    loadingState: document.getElementById('loadingState'),
    
    // Modals
    addRoomModal: document.getElementById('addRoomModal'),
    renameRoomModal: document.getElementById('renameRoomModal'),
    deleteRoomModal: document.getElementById('deleteRoomModal'),
    clearAllModal: document.getElementById('clearAllModal'),
    
    // Modal inputs
    newRoomName: document.getElementById('newRoomName'),
    renameRoomName: document.getElementById('renameRoomName')
  };

  // ============================================
  // Session & Auth Management
  // ============================================

  function getSessionToken() {
    let token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!token) {
      token = generateUUID();
      localStorage.setItem(SESSION_TOKEN_KEY, token);
    }
    return token;
  }

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function checkAuthStatus() {
    // Check if customer is logged in via Shopify
    if (window.Shopify && window.Shopify.customer) {
      state.isLoggedIn = true;
      state.customerId = window.Shopify.customer.id;
    } else {
      state.isLoggedIn = false;
      state.customerId = null;
      state.sessionToken = getSessionToken();
    }

    // Show/hide guest prompt
    if (elements.guestPrompt) {
      elements.guestPrompt.style.display = state.isLoggedIn ? 'none' : 'block';
    }

    // Show/hide add room button (only for logged in users)
    if (elements.addRoomBtn) {
      elements.addRoomBtn.style.display = state.isLoggedIn ? 'flex' : 'none';
    }
  }

  function getRequestPayload(additionalData = {}) {
    return {
      is_logged_in: state.isLoggedIn,
      customer_id: state.customerId,
      session_token: state.sessionToken,
      ...additionalData
    };
  }

  // ============================================
  // API Calls
  // ============================================

  async function apiRequest(endpoint, method = 'GET', body = null) {
    const options = {
      method,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'X-Storefront-Key': STOREFRONT_KEY
      }
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, options);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      showError('Something went wrong. Please try again.');
      throw error;
    }
  }

  async function fetchRooms() {
    // Use GET /wishlist to fetch all rooms (not POST /wishlist/rooms which creates a room)
    // For guests, append session_token as query parameter to avoid CORS issues with headers
    let endpoint = '/wishlist';
    if (!state.isLoggedIn && state.sessionToken) {
      endpoint += `?session_token=${state.sessionToken}`;
    }
    
    const data = await apiRequest(endpoint, 'GET');
    
    // Store session token if returned (for new guests)
    if (data.session_token && !state.isLoggedIn) {
      state.sessionToken = data.session_token;
      localStorage.setItem(SESSION_TOKEN_KEY, data.session_token);
    }

    return data.rooms || [];
  }

  async function fetchRoomProducts(roomId) {
    // GET /wishlist already returns all items in each room, so we can get them from state
    const room = state.rooms.find(r => r.id === roomId);
    if (room && room.items) {
      return room.items.map(item => ({
        id: item.product_id,
        title: item.product?.title || '',
        handle: item.product?.handle || '',
        image: item.product?.primary_image || '',
        featured_image: item.product?.primary_image || '',
        price: item.product?.price || 0
      }));
    }
    return [];
  }

  async function createRoom(roomName) {
    const payload = getRequestPayload({ room_name: roomName });
    const data = await apiRequest('/wishlist/rooms/create', 'POST', payload);
    return data.room;
  }

  async function renameRoom(roomId, newName) {
    const payload = getRequestPayload({ room_id: roomId, room_name: newName });
    await apiRequest('/wishlist/rooms/rename', 'POST', payload);
  }

  async function deleteRoom(roomId) {
    const payload = getRequestPayload({ room_id: roomId });
    await apiRequest('/wishlist/rooms/delete', 'POST', payload);
  }

  async function removeProduct(productId, roomId) {
    const payload = getRequestPayload({ 
      product_id: productId,
      room_id: roomId
    });
    await apiRequest('/wishlist/remove', 'POST', payload);
  }

  async function moveProduct(productId, fromRoomId, toRoomId) {
    const payload = getRequestPayload({
      product_id: productId,
      from_room_id: fromRoomId,
      to_room_id: toRoomId
    });
    await apiRequest('/wishlist/move', 'POST', payload);
  }

  // ============================================
  // UI Rendering
  // ============================================

  function renderRoomTabs(rooms) {
    if (!elements.roomsTabs) return;

    if (rooms.length === 0) {
      elements.roomsTabs.innerHTML = '<p class="rooms-loading">No rooms available</p>';
      return;
    }

    const tabsHTML = rooms.map(room => {
      const isActive = room.id === state.currentRoomId;
      const productCount = room.items_count || (room.items ? room.items.length : 0);
      // Display "My Floors" only for the room named "Default"
      const displayName = room.name === 'Default' ? 'My Floors' : room.name;
      
      return `
        <button 
          class="room-tab ${isActive ? 'active' : ''}" 
          data-room-id="${room.id}"
          data-room-name="${escapeHtml(room.name)}"
          data-is-default="${room.is_default || false}"
        >
          ${escapeHtml(displayName)}
          <span class="product-count">${productCount}</span>
        </button>
      `;
    }).join('');

    elements.roomsTabs.innerHTML = tabsHTML;

    // Attach click handlers
    elements.roomsTabs.querySelectorAll('.room-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const roomId = parseInt(tab.dataset.roomId);
        switchRoom(roomId);
      });
    });
  }

  function renderProducts(products) {
    if (!elements.productsGrid) return;

    if (products.length === 0) {
      elements.productsGrid.style.display = 'none';
      if (elements.emptyState) {
        elements.emptyState.style.display = 'block';
      }
      return;
    }

    elements.productsGrid.style.display = 'grid';
    if (elements.emptyState) {
      elements.emptyState.style.display = 'none';
    }

    const productsHTML = products.map(product => {
      const image = product.image || product.featured_image || '/assets/placeholder.png';
      const price = formatPrice(product.price);
      
      return `
        <a href="/products/${product.handle}" class="wishlist-product-card" data-product-id="${product.id}">
          <div class="product-card-image">
            <img src="${image}" alt="${escapeHtml(product.title)}" loading="lazy">
            <button class="product-card-remove" data-product-id="${product.id}" title="Remove from wishlist">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            ${state.rooms.length > 1 ? `
              <button class="btn-move-product-overlay" data-product-id="${product.id}" title="Move to another room">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="17 11 12 6 7 11"></polyline>
                  <polyline points="17 18 12 13 7 18"></polyline>
                </svg>
              </button>
            ` : ''}
          </div>
          <div class="product-card-info">
            <h3 class="product-card-title">${escapeHtml(product.title)}</h3>
            <p class="product-card-price">${price}</p>
          </div>
        </a>
      `;
    }).join('');

    elements.productsGrid.innerHTML = productsHTML;

    // Update room header to show/hide clear all button
    const room = state.rooms.find(r => r.id === state.currentRoomId);
    if (room) {
      updateRoomHeader(room);
    }

    // Attach event handlers
    elements.productsGrid.querySelectorAll('.product-card-remove').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const productId = parseInt(btn.dataset.productId);
        await handleRemoveProduct(productId);
      });
    });

    elements.productsGrid.querySelectorAll('.btn-move-product-overlay').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const productId = parseInt(btn.dataset.productId);
        await handleMoveProduct(productId);
      });
    });
  }

  function updateRoomHeader(room) {
    if (!room) return;

    if (elements.currentRoomName) {
      // Display "My Floors" only for the room named "Default"
      const displayName = room.name === 'Default' ? 'My Floors' : room.name;
      elements.currentRoomName.textContent = displayName;
    }

    // Show clear all button when there are products
    if (elements.clearAllBtn) {
      const hasProducts = state.products.length > 0;
      elements.clearAllBtn.style.display = hasProducts ? 'flex' : 'none';
    }

    // Show room actions only for custom rooms (logged in users)
    if (elements.roomActionsCustom) {
      const canEdit = state.isLoggedIn && !room.is_default;
      elements.roomActionsCustom.style.display = canEdit ? 'flex' : 'none';
    }
  }

  // ============================================
  // Event Handlers
  // ============================================

  async function switchRoom(roomId) {
    state.currentRoomId = roomId;
    const room = state.rooms.find(r => r.id === roomId);
    
    if (!room) return;

    // Update UI
    updateRoomHeader(room);
    renderRoomTabs(state.rooms);

    // Show loading
    showLoading();

    // Fetch and render products
    try {
      state.products = await fetchRoomProducts(roomId);
      renderProducts(state.products);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      hideLoading();
    }
  }

  async function handleRemoveProduct(productId) {
    try {
      await removeProduct(productId, state.currentRoomId);
      
      // Remove from local state
      state.products = state.products.filter(p => p.id !== productId);
      
      // Update room count
      const room = state.rooms.find(r => r.id === state.currentRoomId);
      if (room) {
        room.items_count = (room.items_count || 1) - 1;
        if (room.items) {
          room.items = room.items.filter(item => item.product_id !== productId);
        }
      }

      // Re-render
      renderProducts(state.products);
      renderRoomTabs(state.rooms);
      updateRoomHeader(room);
      
      showSuccess('Product removed from wishlist');
    } catch (error) {
      console.error('Failed to remove product:', error);
    }
  }

  async function handleClearAll() {
    if (!state.products || state.products.length === 0) {
      showError('No products to remove');
      return;
    }

    try {
      // Remove all products one by one
      const removePromises = state.products.map(product => 
        removeProduct(product.id, state.currentRoomId)
      );
      
      await Promise.all(removePromises);
      
      // Clear local state
      state.products = [];
      
      // Update room count
      const room = state.rooms.find(r => r.id === state.currentRoomId);
      if (room) {
        room.items_count = 0;
        if (room.items) {
          room.items = [];
        }
      }

      // Re-render
      renderProducts(state.products);
      renderRoomTabs(state.rooms);
      updateRoomHeader(room);
      
      hideModal(elements.clearAllModal);
      showSuccess('All products removed from this room');
    } catch (error) {
      console.error('Failed to clear all products:', error);
      showError('Failed to remove all products. Please try again.');
    }
  }

  async function handleMoveProduct(productId) {
    const product = state.products.find(p => p.id === productId);
    if (!product) return;

    // Create room selection modal
    const otherRooms = state.rooms.filter(r => r.id !== state.currentRoomId);
    
    if (otherRooms.length === 0) {
      showError('No other rooms available');
      return;
    }

    const roomOptions = otherRooms.map(room => 
      `<button class="room-option" data-room-id="${room.id}">${escapeHtml(room.name)}</button>`
    ).join('');

    const modalHTML = `
      <div class="modal" id="moveProductModal" style="display: flex;">
        <div class="modal-overlay"></div>
        <div class="modal-content modal-small">
          <div class="modal-header">
            <h3>Move to Room</h3>
            <button class="modal-close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <div class="room-options-list">
              ${roomOptions}
            </div>
          </div>
        </div>
      </div>
    `;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = modalHTML;
    const modal = tempDiv.firstElementChild;
    document.body.appendChild(modal);

    // Add styles for room options
    const style = document.createElement('style');
    style.textContent = `
      .room-options-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .room-option {
        font-family: 'Public Sans', ui-sans-serif, system-ui, sans-serif;
        font-size: 14px;
        font-weight: 500;
        padding: 12px 16px;
        border-radius: 8px;
        border: 1px solid #E5E7EB;
        background: white;
        color: #374151;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: left;
      }
      .room-option:hover {
        background: #F9FAFB;
        border-color: #ACD0D1;
        color: #3D4E5F;
      }
    `;
    document.head.appendChild(style);

    // Handle room selection
    modal.querySelectorAll('.room-option').forEach(btn => {
      btn.addEventListener('click', async () => {
        const toRoomId = parseInt(btn.dataset.roomId);
        
        try {
          await moveProduct(productId, state.currentRoomId, toRoomId);
          
          modal.remove();
          style.remove();
          
          const toRoom = state.rooms.find(r => r.id === toRoomId);
          showSuccess(`Moved to ${toRoom.name}`);
          
          // Refresh page to get latest accurate data
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } catch (error) {
          console.error('Failed to move product:', error);
        }
      });
    });

    // Handle close
    modal.querySelector('.modal-close').addEventListener('click', () => {
      modal.remove();
      style.remove();
    });
    modal.querySelector('.modal-overlay').addEventListener('click', () => {
      modal.remove();
      style.remove();
    });
  }

  // ============================================
  // Room Management
  // ============================================

  function setupRoomManagement() {
    // Add Room
    if (elements.addRoomBtn) {
      elements.addRoomBtn.addEventListener('click', () => {
        showModal(elements.addRoomModal);
        elements.newRoomName.value = '';
        elements.newRoomName.focus();
      });
    }

    document.getElementById('confirmAddRoom')?.addEventListener('click', async () => {
      const roomName = elements.newRoomName.value.trim();
      
      if (!roomName) {
        showError('Please enter a room name');
        return;
      }

      try {
        const newRoom = await createRoom(roomName);
        state.rooms.push(newRoom);
        renderRoomTabs(state.rooms);
        hideModal(elements.addRoomModal);
        showSuccess('Room created successfully');
        
        // Switch to new room
        switchRoom(newRoom.id);
      } catch (error) {
        console.error('Failed to create room:', error);
      }
    });

    // Rename Room
    document.getElementById('renameRoomBtn')?.addEventListener('click', () => {
      const room = state.rooms.find(r => r.id === state.currentRoomId);
      if (!room) return;
      
      elements.renameRoomName.value = room.name;
      showModal(elements.renameRoomModal);
      elements.renameRoomName.focus();
    });

    document.getElementById('confirmRenameRoom')?.addEventListener('click', async () => {
      const newName = elements.renameRoomName.value.trim();
      
      if (!newName) {
        showError('Please enter a room name');
        return;
      }

      try {
        await renameRoom(state.currentRoomId, newName);
        
        const room = state.rooms.find(r => r.id === state.currentRoomId);
        if (room) {
          room.name = newName;
          updateRoomHeader(room);
          renderRoomTabs(state.rooms);
        }
        
        hideModal(elements.renameRoomModal);
        showSuccess('Room renamed successfully');
      } catch (error) {
        console.error('Failed to rename room:', error);
      }
    });

    // Delete Room
    document.getElementById('deleteRoomBtn')?.addEventListener('click', () => {
      showModal(elements.deleteRoomModal);
    });

    document.getElementById('confirmDeleteRoom')?.addEventListener('click', async () => {
      try {
        await deleteRoom(state.currentRoomId);
        
        // Remove from local state
        state.rooms = state.rooms.filter(r => r.id !== state.currentRoomId);
        
        // Switch to first room
        if (state.rooms.length > 0) {
          await switchRoom(state.rooms[0].id);
        }
        
        renderRoomTabs(state.rooms);
        hideModal(elements.deleteRoomModal);
        showSuccess('Room deleted successfully');
      } catch (error) {
        console.error('Failed to delete room:', error);
      }
    });

    // Clear All Products
    if (elements.clearAllBtn) {
      elements.clearAllBtn.addEventListener('click', () => {
        showModal(elements.clearAllModal);
      });
    }

    document.getElementById('confirmClearAll')?.addEventListener('click', async () => {
      await handleClearAll();
    });

    // Modal close handlers
    setupModalCloseHandlers();
  }

  function setupModalCloseHandlers() {
    const modals = [
      { modal: elements.addRoomModal, closeBtn: 'closeAddRoomModal', cancelBtn: 'cancelAddRoom' },
      { modal: elements.renameRoomModal, closeBtn: 'closeRenameRoomModal', cancelBtn: 'cancelRenameRoom' },
      { modal: elements.deleteRoomModal, closeBtn: 'closeDeleteRoomModal', cancelBtn: 'cancelDeleteRoom' },
      { modal: elements.clearAllModal, closeBtn: 'closeClearAllModal', cancelBtn: 'cancelClearAll' }
    ];

    modals.forEach(({ modal, closeBtn, cancelBtn }) => {
      if (!modal) return;

      document.getElementById(closeBtn)?.addEventListener('click', () => hideModal(modal));
      document.getElementById(cancelBtn)?.addEventListener('click', () => hideModal(modal));
      
      modal.querySelector('.modal-overlay')?.addEventListener('click', () => hideModal(modal));
    });
  }

  // ============================================
  // Utility Functions
  // ============================================

  function showModal(modal) {
    if (modal) modal.style.display = 'flex';
  }

  function hideModal(modal) {
    if (modal) modal.style.display = 'none';
  }

  function showLoading() {
    if (elements.loadingState) elements.loadingState.style.display = 'block';
    if (elements.productsGrid) elements.productsGrid.style.display = 'none';
    if (elements.emptyState) elements.emptyState.style.display = 'none';
  }

  function hideLoading() {
    if (elements.loadingState) elements.loadingState.style.display = 'none';
  }

  function showSuccess(message) {
    // You can implement a toast notification here
    console.log('Success:', message);
  }

  function showError(message) {
    // You can implement a toast notification here
    console.error('Error:', message);
    alert(message);
  }

  function formatPrice(price) {
    if (!price) return '';
    const amount = typeof price === 'number' ? price / 100 : parseFloat(price);
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ============================================
  // Initialization
  // ============================================

  async function init() {
    try {
      // Check auth status
      checkAuthStatus();

      // Setup room management
      setupRoomManagement();

      // Show loading
      showLoading();

      // Fetch rooms
      state.rooms = await fetchRooms();

      // Render rooms
      renderRoomTabs(state.rooms);

      // Switch to first room (or default)
      if (state.rooms.length > 0) {
        const defaultRoom = state.rooms.find(r => r.name === 'Default') || state.rooms[0];
        await switchRoom(defaultRoom.id);
      } else {
        hideLoading();
        if (elements.emptyState) {
          elements.emptyState.style.display = 'block';
        }
      }

    } catch (error) {
      console.error('Initialization failed:', error);
      hideLoading();
      showError('Failed to load wishlist. Please refresh the page.');
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
